import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * "Showing X–Y of Z" + prev/next + page-size selector.
 *
 * Designed to be dropped under any `<table>` produced by `co-card`/`data-table`.
 * Renders nothing when there are zero rows (use the page's empty-state instead).
 */
export function TablePagination({
  page, pageSize, totalRows, totalPages,
  start, end,
  setPage, setPageSize,
  pageSizeOptions = [25, 50, 100],
  testId = "table-pagination",
}) {
  if (totalRows === 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-slate-100 bg-white text-xs text-slate-600" data-testid={testId}>
      <div data-testid={`${testId}-summary`}>
        Showing <span className="font-semibold text-slate-800">{start}</span>–
        <span className="font-semibold text-slate-800">{end}</span> of{" "}
        <span className="font-semibold text-slate-800">{totalRows.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5" data-testid={`${testId}-page-size-label`}>
          <span className="text-slate-500">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value)}
            className="border border-slate-200 rounded-md px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid={`${testId}-page-size`}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>
          <span className="px-2 text-slate-500" data-testid={`${testId}-page`}>
            Page <span className="font-semibold text-slate-800">{page}</span> / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testId}-next`}
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
